import { useCallback, useMemo } from 'react';

import { EnhancedReturnAdapterForFulfillmentPlannerContext } from '../../../fulfillment-planner/adapters/enhanced-return';
import { FulfillmentPlanRestrictionsContextProvider } from '../fulfillment-plan/restrictions-context';
import { OrderDetailsSelectedContent } from '../selected-content/order-details-selected-content';
import { ReturnConfigProvider } from './return-config-provider';
import { ReturnDetailsHeader } from './sections/return-details-header';
import { ReturnDetailsInfo } from './sections/return-details-info';
import { ShipmentConfigurator } from './sections/shipment-configurator';
import { ReturnDetailsFulfillment } from './sections/return-details-fulfillment';
import { ReturnRateCard } from './sections/return-rate-card';
import { ActivityLog } from './components/activity-log';
import { useSalesOrderIdContext } from '../../context/sales-order-id-context-provider';
import { useCreateReturnContext } from '../context/create-return-provider';
import { useReturnDataContext } from './return-data-provider';
import { useEnhancedReturnsContext } from '../context/enhanced-returns-context-provider';
import { isAwaitingShipment } from '../../../store/returns/returns.helpers';

export const ReturnDetailsPage = () => {
  const salesOrderId = useSalesOrderIdContext();
  const { returnFulfillmentPlanId } = useCreateReturnContext();
  const { loadEnhancedReturns } = useEnhancedReturnsContext();
  const { requestDetails, reloadRequestDetails } = useReturnDataContext();

  const returnId = requestDetails?.id ?? '';
  const isShipmentNeeded = isAwaitingShipment(requestDetails);

  const customerAddress = useMemo(() => {
    const shippingAddress = requestDetails?.shippingAddress ?? {};
    const fullName = shippingAddress?.name ?? '';
    const firstName = shippingAddress?.firstName ?? '';
    const lastName = shippingAddress?.lastName ?? '';
    return {
      ...shippingAddress,
      name: fullName || [firstName, lastName].filter(Boolean).join(' '),
    };
  }, [requestDetails]);

  const onReturnConfirmed = useCallback(() => {
    reloadRequestDetails();
    loadEnhancedReturns();
  }, [reloadRequestDetails, loadEnhancedReturns]);

  return (
    <ReturnConfigProvider customerAddress={customerAddress} onReturnConfirmed={onReturnConfirmed} returnId={returnId}>
      <EnhancedReturnAdapterForFulfillmentPlannerContext
        fulfillmentPlanId={returnFulfillmentPlanId!}
        isAwaitingShipment={isShipmentNeeded}
        returnId={returnId}
        salesOrderId={salesOrderId}
      >
        <FulfillmentPlanRestrictionsContextProvider fulfillmentPlanId={returnFulfillmentPlanId!} salesOrderId={salesOrderId}>
          <OrderDetailsSelectedContent
            fulfillmentPlannerSections={isShipmentNeeded ? <ShipmentConfigurator /> : <ReturnDetailsFulfillment />}
            headerContent={<ReturnDetailsHeader />}
            primaryShipmentSections={<ReturnDetailsInfo />}
            rateCard={isShipmentNeeded && <ReturnRateCard />}
            secondaryShipmentSections={<ActivityLog />}
          />
        </FulfillmentPlanRestrictionsContextProvider>
      </EnhancedReturnAdapterForFulfillmentPlannerContext>
    </ReturnConfigProvider>
  );
};
